import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useAsyncList } from 'react-stately';
import { ListProjectAdvancedRequest } from '../../../../services/soap/project/requests/ListProjectAdvancedRequest';
import { GetFormPanelRequest } from '../../../../services/soap/features/requests/GetFormPanelRequest';
import { ListFormSettingsRequest } from '../../../../services/soap/features/requests/ListFormSettingsRequest';
import { GetFormSettingsRequest } from '../../../../services/soap/features/requests/GetFormSettingsRequest';
import { ListRegisterRequest } from '../../../../services/soap/features/requests/ListRegisterRequest';
import { ListCustomViewsRequest } from '../../../../services/soap/features/requests/ListCustomViewsRequest';
import { SoapUtils } from '../../../../utils/SoapUtils';
import { FPAData, FPADataFactory } from '../../../../infra/protected/FPA/FPAData';
import { CustomView } from '../../../../services/soap/dataObjects/CustomView';
import { SettingItem } from '../../../../services/soap/features/responses/GetFormSettingsResponse';
import { Order } from '../../../../services/soap/dataObjects/Order';
import type { ProjectItem, ProjectStatus, ColumnDefinition, FilterColumn, QueryParams } from '../types';
import type { IProjectService } from '../../../../services/soap/project/ProjectService';
import type { IFeatureService } from '../../../../services/soap/features/FeatureService';
import type { ProjectAdvanced } from '../../../../services/soap/project/responses/ListProjectAdvancedResponse';

export interface UseProjectActivitiesDataParams {
  selectedApp: string | null;
  store: { Server: string; SessionId: string };
  projectService: IProjectService;
  featureService: IFeatureService;
  currencies: { id: string | number; code: string }[];
  formatNumber: (value: number) => string;
  activityStatusMapping: Record<string, { name: string; color: string }> | null;
  projectStatusList: ProjectStatus[];
  rowsData: ProjectItem[];
  selectedKeys: Set<string | number> | undefined;
  stopLoadDataRef: React.MutableRefObject<boolean>;
  actions: {
    appendRowsData: (payload: ProjectItem[]) => void;
    setRowsData: (payload: ProjectItem[]) => void;
    setSelectedKeys: (payload: Set<string | number> | undefined) => void;
    setSelectedDataRow: (payload: ProjectItem | Record<string, never>) => void;
    setSelectedItem: (payload: FPAData | null) => void;
    setDefinition: (payload: unknown) => void;
    setShowLoader: (payload: boolean) => void;
    setShowLoaderDetail: (payload: boolean) => void;
  };
  onError?: (message: string) => void;
}

function processJsonData(data: ProjectItem[]): ProjectItem[] {
  return data.map((item: ProjectItem) => {
    const newItem: Record<string, unknown> = { ...item };
    Object.keys(newItem).forEach(key => {
      const value = newItem[key];
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const objectKeys = Object.keys(value as Record<string, unknown>);
        if (objectKeys.length === 1 && Array.isArray((value as Record<string, unknown>)[objectKeys[0]])) {
          newItem[key] = ((value as Record<string, unknown>)[objectKeys[0]] as unknown[]).join(', ');
        } else if (objectKeys.length === 1 && typeof (value as Record<string, unknown>)[objectKeys[0]] !== 'object') {
          newItem[key] = (value as Record<string, unknown>)[objectKeys[0]];
        }
      } else if (Array.isArray(value)) {
        newItem[key] = (value as unknown[]).join(', ');
      }
    });
    return newItem as ProjectItem;
  });
}

function sanitizeSelectedFields(rawFields: string[] | undefined): string[] {
  return Array.from(
    new Set(
      (rawFields || [])
        .map(f => String(f || ''))
        .map(f => {
          const [left] = f.split('|');
          return left?.trim() ?? '';
        })
        .filter(f => f !== '')
    )
  );
}

export interface UseProjectActivitiesDataReturn {
  listProjectList: ReturnType<typeof useAsyncList<ProjectItem>>;
  columns: React.MutableRefObject<ColumnDefinition[]>;
  columnsState: ColumnDefinition[];
  projectListKey: React.MutableRefObject<string>;
  queryParams: React.MutableRefObject<QueryParams>;
  reload: (filterParams?: QueryParams) => void;
  fetchProjectLayout: (selectedRow: ProjectItem) => Promise<void>;
  formSettings: SettingItem[];
  customViews: CustomView[];
  hasMoreCustomViews: boolean;
  loadMoreCustomViews: () => Promise<void>;
  refreshCustomViews: () => Promise<void>;
  isViewsLoading: boolean;
}

const PAGE_SIZE = 50;

export function useProjectActivitiesData(params: UseProjectActivitiesDataParams): UseProjectActivitiesDataReturn {
  const {
    selectedApp,
    store,
    projectService,
    featureService,
    currencies,
    formatNumber,
    activityStatusMapping,
    projectStatusList,
    rowsData,
    selectedKeys,
    stopLoadDataRef,
    actions,
    onError,
  } = params;

  const queryParams = useRef<QueryParams>({ FILTER: { COL: [] } });
  const projectListKey = useRef<string>('');
  const totalProjectListRec = useRef<number>(0);
  const offSetLimit = useRef<number>(0);
  const columns = useRef<ColumnDefinition[]>([]);
  const columnNames = useRef<string[]>([]);
  const currencyList = useRef<{ id: string | number; code: string }[]>([]);

  const [formSettings, setFormSettings] = useState<SettingItem[]>([]);
  const [columnsState, setColumnsState] = useState<ColumnDefinition[]>([]);
  const [customViews, setCustomViews] = useState<CustomView[]>([]);
  const [customViewsOffset, setCustomViewsOffset] = useState<number>(0);
  const [hasMoreCustomViews, setHasMoreCustomViews] = useState<boolean>(false);
  const [isViewsLoading, setIsViewsLoading] = useState<boolean>(true);
  const [reloadTrigger, setReloadTrigger] = useState(0);

  // Reset app-scoped state only when selectedApp changes. Do NOT depend on currencies
  // or customViews will be cleared when currencies loads asynchronously (bug: empty
  // state flicker after custom views have loaded).
  useEffect(() => {
    if (!selectedApp) return;
    queryParams.current = { FILTER: { COL: [] } };
    projectListKey.current = '';
    totalProjectListRec.current = 0;
    offSetLimit.current = 0;
    currencyList.current = currencies.map(item => ({ id: item.id, code: item.code }));
    setCustomViews([]);
    setHasMoreCustomViews(false);
    setCustomViewsOffset(0);
    setFormSettings([]);
  }, [selectedApp]); // eslint-disable-line react-hooks/exhaustive-deps -- currencies intentionally omitted; synced below

  // Sync currencyList when currencies loads (e.g. from usePreloadAssets). Must NOT clear
  // customViews here or they get wiped when currencies loads after custom views fetch.
  useEffect(() => {
    if (!selectedApp) return;
    currencyList.current = currencies.map(item => ({ id: item.id, code: item.code }));
  }, [selectedApp, currencies]);

  const fetchFormSettings = useCallback(
    async (settingId: string): Promise<{ columns: ColumnDefinition[]; settings: SettingItem[] }> => {
      if (settingId === '') {
        const resFormSettings = await featureService.listFormSettings(new ListFormSettingsRequest(store.Server, store.SessionId));
        const filteredData = resFormSettings.SETTING.filter(
          (item) => item.formId === 'ListProjectAdvanced' && String(item.isDefault) === '1'
        );
        if (filteredData?.length > 0) {
          settingId = filteredData[0].id.toString();
        }
      }
      const response = await featureService.getFormSettings(new GetFormSettingsRequest(store.Server, store.SessionId, settingId));
      if (response?.result === 'OK' && response?.count > 0) {
        const allSettings = response.SETTING[0]?.ITEM?.filter(item => String(item.visibility) === '1') ?? [];
        const result = allSettings.map(item => ({
          key: item.itemname.includes('|') ? item.itemname.split('|')[1] : item.itemname,
          name: item.caption?.trim() ? item.caption : item.name,
        }));
        columnNames.current = result.map(item => item.key);
        result.forEach(item => {
          if (item.key.includes('.')) {
            item.key = item.key.replace(/\./g, '_');
          }
        });
        columns.current = result;
        setColumnsState(result);
        setFormSettings(allSettings);
        return { columns: result, settings: allSettings };
      }
      return { columns: [], settings: [] };
    },
    [featureService, store.Server, store.SessionId]
  );

  const fetchProjectListKey = useCallback(async () => {
    if (!selectedApp) return '';
    const resListRegister = await featureService.listRegister(
      new ListRegisterRequest(store.Server, store.SessionId, undefined, undefined, undefined, undefined, Number(selectedApp))
    );
    projectListKey.current =
      resListRegister?.ROWS?.find((row: { ROW: { key: string; value: string } }) => row.ROW.key === 'FPATypeCategoryForListProject')
        ?.ROW.value ?? '';
    const settingId =
      resListRegister?.ROWS?.find((row: { ROW: { key: string; value: string } }) => row.ROW.key === 'formSetting')?.ROW.value ?? '';
    await fetchFormSettings(settingId);
    return projectListKey.current;
  }, [selectedApp, featureService, store.Server, store.SessionId, fetchFormSettings]);

  /**
   * Normalizes "relative_preset" filters into backend-supported "greater"/"less" date conditions.
   *
   * The UI stores relative date presets (e.g., "Today", "Last 7 Days", "This Month") as:
   *   type: "relative_preset"
   *   value: preset ID (e.g., "today", "last_7_days", "this_month")
   *
   * The backend expects date range filters with:
   *   - one with type "greater" for the start date (inclusive)
   *   - one with type "less" for the end date (inclusive, using 23:59:59)
   *
   * This helper converts each relative preset into concrete date ranges based on the current date,
   * formatting dates using SoapUtils.toDateForXml for API compatibility.
   */
  const normalizeRelativePresetFilters = useCallback(
    (params: QueryParams): QueryParams => {
      const sourceCols = params.FILTER?.COL ?? [];
      const normalizedCols: FilterColumn[] = [];

      sourceCols.forEach(col => {
        const type = (col.type as unknown) as string;

        if (type !== 'relative_preset') {
          normalizedCols.push(col);
          return;
        }

        const presetId = String(col.value || '').trim();
        if (!presetId) {
          return;
        }

        const now = moment();
        let startDate: Date | undefined;
        let endDate: Date | undefined;

        switch (presetId) {
          case 'today': {
            startDate = now.startOf('day').toDate();
            endDate = now.endOf('day').toDate();
            break;
          }
          case 'yesterday': {
            const yesterday = now.clone().subtract(1, 'day');
            startDate = yesterday.startOf('day').toDate();
            endDate = yesterday.endOf('day').toDate();
            break;
          }
          case 'tomorrow': {
            const tomorrow = now.clone().add(1, 'day');
            startDate = tomorrow.startOf('day').toDate();
            endDate = tomorrow.endOf('day').toDate();
            break;
          }
          case 'last_7_days': {
            startDate = now.clone().subtract(7, 'days').startOf('day').toDate();
            endDate = now.endOf('day').toDate();
            break;
          }
          case 'next_7_days': {
            startDate = now.startOf('day').toDate();
            endDate = now.clone().add(7, 'days').endOf('day').toDate();
            break;
          }
          case 'last_30_days': {
            startDate = now.clone().subtract(30, 'days').startOf('day').toDate();
            endDate = now.endOf('day').toDate();
            break;
          }
          case 'next_30_days': {
            startDate = now.startOf('day').toDate();
            endDate = now.clone().add(30, 'days').endOf('day').toDate();
            break;
          }
          case 'last_month': {
            const lastMonth = now.clone().subtract(1, 'month');
            startDate = lastMonth.startOf('month').startOf('day').toDate();
            endDate = lastMonth.endOf('month').endOf('day').toDate();
            break;
          }
          case 'this_month': {
            startDate = now.clone().startOf('month').startOf('day').toDate();
            endDate = now.clone().endOf('month').endOf('day').toDate();
            break;
          }
          case 'next_month': {
            const nextMonth = now.clone().add(1, 'month');
            startDate = nextMonth.startOf('month').startOf('day').toDate();
            endDate = nextMonth.endOf('month').endOf('day').toDate();
            break;
          }
          case 'last_quarter': {
            const lastQuarter = now.clone().subtract(1, 'quarter');
            startDate = lastQuarter.startOf('quarter').startOf('day').toDate();
            endDate = lastQuarter.endOf('quarter').endOf('day').toDate();
            break;
          }
          case 'this_quarter': {
            startDate = now.clone().startOf('quarter').startOf('day').toDate();
            endDate = now.clone().endOf('quarter').endOf('day').toDate();
            break;
          }
          case 'next_quarter': {
            const nextQuarter = now.clone().add(1, 'quarter');
            startDate = nextQuarter.startOf('quarter').startOf('day').toDate();
            endDate = nextQuarter.endOf('quarter').endOf('day').toDate();
            break;
          }
          case 'last_year': {
            const lastYear = now.clone().subtract(1, 'year');
            startDate = lastYear.startOf('year').startOf('day').toDate();
            endDate = lastYear.endOf('year').endOf('day').toDate();
            break;
          }
          case 'this_year': {
            startDate = now.clone().startOf('year').startOf('day').toDate();
            endDate = now.clone().endOf('year').endOf('day').toDate();
            break;
          }
          case 'next_year': {
            const nextYear = now.clone().add(1, 'year');
            startDate = nextYear.startOf('year').startOf('day').toDate();
            endDate = nextYear.endOf('year').endOf('day').toDate();
            break;
          }
          default: {
            console.warn(`Unknown relative preset ID: ${presetId}`);
            return;
          }
        }

        if (startDate) {
          const formattedStartDate = SoapUtils.toDateForXml(startDate, true);
          if (formattedStartDate) {
            normalizedCols.push({
              ...(col as unknown as Record<string, unknown>),
              type: 'greater',
              value: formattedStartDate,
            } as FilterColumn);
          }
        }

        if (endDate) {
          const endDateMoment = moment(endDate);
          const formattedEndDate = endDateMoment.format('YYYY-MM-DD') + 'T23:59:59';
          normalizedCols.push({
            ...(col as unknown as Record<string, unknown>),
            type: 'less',
            value: formattedEndDate,
          } as FilterColumn);
        }
      });

      return {
        ...params,
        FILTER: {
          ...params.FILTER,
          COL: normalizedCols,
        },
      };
    },
    []
  );

  const fetchCustomViews = useCallback(
    async (offset: number = 0, append: boolean = false) => {
      if (!selectedApp) return;

      try {
        const adminTreeValue = (store as { session?: { sessionInfo?: { adminTree?: boolean | string } } }).session?.sessionInfo?.adminTree;
        const isAdmin = adminTreeValue === true || adminTreeValue === 'true';

        const response = await featureService.listCustomViews(
          new ListCustomViewsRequest(store.Server, store.SessionId, {
            limit: 100,
            offset,
            currentUserOnly: !isAdmin,
            FILTER: {
              COL: [{ name: 'applicationId', type: 'equals' as const, value: selectedApp }],
            },
          })
        );

        if (response?.result === 'OK' && response?.CUSTOM_VIEW) {
          const views = Array.isArray(response.CUSTOM_VIEW) ? response.CUSTOM_VIEW : [response.CUSTOM_VIEW];
          if (append) {
            setCustomViews(prev => [...prev, ...views]);
          } else {
            setCustomViews(views);
          }
          setHasMoreCustomViews(response.count > offset + views.length);
          setCustomViewsOffset(offset + views.length);
        } else {
          if (!append) setCustomViews([]);
          setHasMoreCustomViews(false);
        }
      } catch (error) {
        console.error('Error fetching custom views:', error);
        if (!append) setCustomViews([]);
        setHasMoreCustomViews(false);
      }
    },
    [featureService, selectedApp, store]
  );

  const loadMoreCustomViews = useCallback(async () => {
    if (!hasMoreCustomViews) return;
    await fetchCustomViews(customViewsOffset, true);
  }, [fetchCustomViews, customViewsOffset, hasMoreCustomViews]);

  const refreshCustomViews = useCallback(async () => {
    await fetchCustomViews(0, false);
  }, [fetchCustomViews]);

  const extractCustomViewFieldsAndOrder = useCallback(
    (
      cv: CustomView[],
      params: QueryParams
    ): { fields?: string[]; order?: Order } | undefined => {
      const stateFilter = params.FILTER.COL.find(col => col.name === 'state');
      if (!stateFilter || !stateFilter.value) return undefined;

      const viewId = String(stateFilter.value);
      const selectedView = cv.find(v => String(v.id ?? '') === viewId);
      if (!selectedView?.definition) return undefined;

      try {
        const definition = JSON.parse(selectedView.definition);
        const fields: string[] | undefined = definition.columns
          ?.filter((col: { isVisible?: boolean }) => col.isVisible !== false)
          ?.map((col: { itemname?: string }) => col.itemname)
          ?.filter((itemname: string) => itemname != null && itemname !== '');

        const order: Order | undefined =
          definition.sorting?.length > 0
            ? {
                COL: definition.sorting.map(
                  (sort: { itemname?: string; level?: string }) => {
                    let sortName = sort.itemname || '';
                    if (sortName.includes('|')) {
                      const parts = sortName.split('|');
                      sortName = parts[1]?.trim() || sortName;
                    }
                    return {
                      name: sortName,
                      desc: sort.level === 'Descending' ? 'true' : 'false',
                    };
                  }
                ),
              }
            : undefined;

        return { fields, order };
      } catch {
        return undefined;
      }
    },
    []
  );

  const buildApiFilters = useCallback((params: QueryParams) => {
    return params.FILTER.COL.filter(col => col.name !== 'state');
  }, []);

  const getSelectedFields = useCallback(
    (params: QueryParams) => {
      const cvData = extractCustomViewFieldsAndOrder(customViews, params);
      const rawFields =
        cvData?.fields && cvData.fields.length > 0 ? cvData.fields : columnNames.current;

      return {
        fields: sanitizeSelectedFields(rawFields),
        order: cvData?.order,
      };
    },
    [customViews, extractCustomViewFieldsAndOrder]
  );

  const fetchProjectCount = useCallback(async () => {
    let projectActivityValue_current = projectListKey.current;
    if (!projectActivityValue_current || projectActivityValue_current === '') {
      projectActivityValue_current = await fetchProjectListKey();
    }
    const fpaTypeCategoryExists = queryParams.current.FILTER.COL.some(col => col.name === 'fpaTypeCategory');
    if (!fpaTypeCategoryExists) {
      queryParams.current.FILTER.COL.push({
        name: 'fpaTypeCategory',
        type: 'any',
        value: projectActivityValue_current,
      });
    }
    projectListKey.current = projectActivityValue_current;
    const uniqueCOL: QueryParams['FILTER']['COL'] = [];
    const typeName = new Set<string>();
    queryParams.current.FILTER.COL.forEach(item => {
      if (!typeName.has(item.name)) {
        typeName.add(item.name);
        uniqueCOL.push(item);
      }
    });
    queryParams.current.FILTER.COL = uniqueCOL;

    const normalized = normalizeRelativePresetFilters(queryParams.current);
    const apiFilterCol = buildApiFilters(normalized);
    const { fields, order } = getSelectedFields(queryParams.current);

    const requestPayload: {
      countOnly: '1';
      FILTER: QueryParams['FILTER'];
      FIELDS?: { field: string[] };
      ORDER?: Order;
    } = {
      countOnly: '1',
      FILTER: { COL: apiFilterCol },
    };

    if (fields.length > 0) {
      requestPayload.FIELDS = { field: fields };
    }
    if (order) {
      requestPayload.ORDER = order;
    }

    const projectRes = await projectService.listProjectAdvanced(
      new ListProjectAdvancedRequest(store.Server, store.SessionId, requestPayload)
    );
    if (projectRes?.result === 'OK') {
      totalProjectListRec.current = projectRes.count !== 0 ? projectRes.count : 0;
      return projectRes.count !== 0 ? projectRes.count : 0;
    }
  }, [
    fetchProjectListKey,
    normalizeRelativePresetFilters,
    buildApiFilters,
    getSelectedFields,
    projectService,
    store.Server,
    store.SessionId,
  ]);

  const fetchProjects = useCallback(async () => {
    try {
      if (stopLoadDataRef.current) {
        return rowsData;
      }
      currencyList.current = currencies.map(item => ({ id: item.id, code: item.code }));

      const normalized = normalizeRelativePresetFilters(queryParams.current);
      const apiFilterCol = buildApiFilters(normalized);
      const { fields, order: customViewOrder } = getSelectedFields(queryParams.current);
      const order =
        customViewOrder ?? { COL: [{ name: 'modified', desc: 'true' as const }] };

      const projectRes = await projectService.listProjectAdvanced(
        new ListProjectAdvancedRequest(store.Server, store.SessionId, {
          limit: PAGE_SIZE,
          offset: offSetLimit.current ?? 0,
          FILTER: { COL: apiFilterCol },
          FIELDS: { field: fields },
          ORDER: order,
        })
      );
      if (projectRes?.result === 'OK' && projectRes?.count > 0) {
        const listProject = (projectRes?.PROJECTS?.PROJECT ?? []) as unknown as ProjectItem[];
        listProject.forEach(item => {
          item.chargeable = item.chargeable === '1' ? 'Yes' : 'No';
          const currency = currencyList.current.find(c => String(c.id) === String(item.currency));
          if (item.amountWithoutVat) {
            item.amountWithoutVat = currency
              ? currency.code + ' ' + formatNumber(Number(String(item.amountWithoutVat).split('.')[0].replace(/\D/g, '')))
              : item.amountWithoutVat;
          } else {
            item.amountWithoutVat = 0;
          }
        });
        actions.appendRowsData(listProject);
        if (listProject?.length > 0 && selectedKeys === undefined) {
          actions.setSelectedKeys(new Set([listProject[0].id]));
          actions.setSelectedDataRow(listProject[0]);
          await fetchProjectLayoutRef.current?.(listProject[0]);
          return listProject;
        }
        return listProject;
      } else {
        actions.setRowsData([]);
        return [];
      }
    } finally {
      actions.setShowLoader(false);
    }
  }, [
    stopLoadDataRef,
    rowsData,
    projectService,
    store.Server,
    store.SessionId,
    formatNumber,
    currencies,
    selectedKeys,
    actions,
    getSelectedFields,
    buildApiFilters,
    normalizeRelativePresetFilters,
  ]);

  const fetchProjectLayoutRef = useRef<((row: ProjectItem) => Promise<void>) | undefined>(undefined);

  const fetchProjectLayout = useCallback(
    async (selectedRow: ProjectItem) => {
      try {
        const resSelectedItem = FPADataFactory.createFromProjectAdvanced(
          selectedRow as unknown as ProjectAdvanced,
          activityStatusMapping ?? undefined
        );
        actions.setSelectedItem(resSelectedItem);
        actions.setSelectedDataRow(selectedRow);
        const typeId = selectedRow.projectType;
        const layout = await featureService.getFormPanel(
          new GetFormPanelRequest(store.Server, store.SessionId, String(typeId))
        );
        if (layout.count > 0) {
          const scheme = SoapUtils.parseXmlString2(layout.ROW.SCHEME);
          const layoutObject = JSON.parse(scheme.ActivityPanelSettings.layoutObject);
          actions.setDefinition(layoutObject);
        } else {
          actions.setDefinition(null);
        }
      } catch (err) {
        console.error('getLayout error: ', err);
        onError?.(err instanceof Error ? err.message : 'Failed to load layout');
      } finally {
        actions.setShowLoaderDetail(false);
      }
    },
    [activityStatusMapping, featureService, store.Server, store.SessionId, actions, onError]
  );

  fetchProjectLayoutRef.current = fetchProjectLayout;

  const listProjectList = useAsyncList<ProjectItem>({
    async load({ cursor, signal }) {
      try {
        // Only gate on selectedApp; allow loads even if currencyList or projectStatusList
        // are still initializing so custom view changes always trigger API calls.
        if (!selectedApp) {
          return { items: [], cursor: undefined };
        }
        if (isViewsLoading) {
          return { items: [], cursor: undefined };
        }
        if (signal?.aborted) {
          return { items: [], cursor: undefined };
        }
        const currentPage = cursor ? parseInt(cursor, 10) : 0;
        const estimatedLoadedCount = currentPage * PAGE_SIZE;
        if (cursor && estimatedLoadedCount >= totalProjectListRec.current) {
          return { items: rowsData, cursor: undefined };
        }
        const totalRecords = currentPage === 0 ? await fetchProjectCount() : totalProjectListRec.current;
        const offset = Math.min(PAGE_SIZE * currentPage, totalRecords ?? 0);
        offSetLimit.current = offset;
        const items = await fetchProjects();
        const processedData = processJsonData(items ?? []);
        processedData.forEach(item => {
          Object.keys(item).forEach(key => {
            if (key.includes('.')) {
              item[key.replace(/\./g, '_')] = item[key];
              delete item[key];
            }
          });
        });
        const hasMoreItems = offset + (processedData?.length || 0) < (totalRecords ?? 0);
        const nextCursor = hasMoreItems ? String(currentPage + 1) : undefined;
        return { items: processedData || [], cursor: nextCursor };
      } catch (error) {
        console.error('Error loading activity list:', error);
        onError?.(error instanceof Error ? error.message : 'Error loading data');
        return { items: [], cursor: undefined };
      }
    },
    async sort({ items, sortDescriptor }) {
      if (!sortDescriptor) return { items };
      const { column, direction } = sortDescriptor;
      return {
        items: [...items].sort((a: ProjectItem, b: ProjectItem) => {
          const aValue = a[column as keyof ProjectItem] as unknown;
          const bValue = b[column as keyof ProjectItem] as unknown;
          let result = 0;
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            result = aValue.localeCompare(bValue);
          } else {
            const av = aValue as string | number;
            const bv = bValue as string | number;
            if (av < bv) result = -1;
            else if (av > bv) result = 1;
          }
          return direction === 'descending' ? -result : result;
        }),
      };
    },
    getKey: item => item.id,
  });

  const reload = useCallback((filterParams?: QueryParams) => {
    if (filterParams) {
      queryParams.current = filterParams;
    }
    stopLoadDataRef.current = false;
    totalProjectListRec.current = 0;
    offSetLimit.current = 0;
    setReloadTrigger((t) => t + 1);
  }, [stopLoadDataRef]);

  // Trigger list reload in an effect so useAsyncList sees the latest load() closure (fixes view switch not firing ListProjectAdvancedRequest)
  useEffect(() => {
    if (reloadTrigger === 0) return;
    listProjectList.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when reloadTrigger changes; listProjectList is from current render
  }, [reloadTrigger]);

  // Pre-fetch form settings when selectedApp changes so CustomViewDialog has columns
  // before the user opens it (formSettings was previously only populated when the
  // project list loaded, causing empty "All Columns" in the dialog)
  useEffect(() => {
    if (!selectedApp) return;
    fetchProjectListKey();
  }, [selectedApp, fetchProjectListKey]);

  // Fetch CustomViews when selectedApp changes
  useEffect(() => {
    if (!selectedApp) {
      setIsViewsLoading(false);
      return;
    }
    let cancelled = false;
    setIsViewsLoading(true);
    fetchCustomViews(0, false).finally(() => {
      if (!cancelled) setIsViewsLoading(false);
    });
    return () => { cancelled = true; };
  }, [selectedApp, fetchCustomViews]);

  return {
    listProjectList,
    columns,
    columnsState,
    projectListKey,
    queryParams,
    reload,
    fetchProjectLayout,
    formSettings,
    customViews,
    hasMoreCustomViews,
    loadMoreCustomViews,
    refreshCustomViews,
    isViewsLoading,
  };
}
